import React from 'react';
import { Card, Progress, Button, List, Avatar, Space, Tooltip } from 'antd';
import { 
  CheckCircleOutlined, 
  RocketOutlined,
  BuildOutlined,
  TeamOutlined,
  BankOutlined,
  MinusOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSetupTodo } from '../../../hooks/useSetupTodo';
import { SetupTodoItem } from '../../../types/company.types';
import { useTourSetupSync } from '../../../hooks/useTourSetupSync'; // Yeni merkezi kontrol sistemi
import './SetupTodo.scss';

const SetupTodo: React.FC = () => {
  const navigate = useNavigate();

  // Veri ve durumları ilgili hook'lardan al
  const { 
    todoItems, 
    loading, 
    initialLoad,
    dataReady,
    progress, 
    completedCount, 
    totalCount,
  } = useSetupTodo();

  const {
    showGuide,
    showMinimizedGuide,
    minimizeGuideAndSave,
    restoreGuideAndSave,
  } = useTourSetupSync(); // Tüm UI mantığı artık buradan geliyor
  
  // Extra güvenlik kontrolü - eğer tüm todo'lar tamamlandıysa kesinlikle gösterme
  const allTodosCompleted = todoItems.length > 0 && todoItems.every(item => item.completed);

  // Icon mapping
  const getIcon = (iconName: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      'building': <BankOutlined />,
      'category': <BuildOutlined />,
      'users': <TeamOutlined />,
    };
    return iconMap[iconName] || <RocketOutlined />;
  };

  // Item'a tıklama
  const handleItemClick = (item: SetupTodoItem) => {
    if (!item.completed) {
      navigate(item.route);
    }
  };

  // Veri hazır değilse hiçbir şey render etme
  if (!dataReady || initialLoad || loading) {
    return null;
  }
  
  // Eğer tüm todo'lar tamamlandıysa kesinlikle hiçbir şey gösterme
  if (allTodosCompleted || progress === 100) {
    return null;
  }

  // Küçültülmüş görünüm
  if (showMinimizedGuide) {
    return (
      <div className="setup-todo-minimized" onClick={restoreGuideAndSave}>
        <div className="minimized-icon">
          <CheckCircleOutlined style={{ color: '#7338a0', fontSize: '20px' }} />
          <span className="minimized-text">Kurulum Rehberi</span>
        </div>
      </div>
    );
  }
  
  // Normal (büyük) görünüm
  if (!showGuide) {
    return null;
  }

  return (
    <div className="setup-todo-container">
      <Card
        className="setup-todo-card"
        size="small"
        title={
          <div className="setup-todo-header">
            <Space>
              <RocketOutlined className="setup-todo-main-icon" />
              <div>
                <div className="setup-todo-title">Kurulum Rehberi</div>
                <div className="setup-todo-subtitle">
                  {completedCount}/{totalCount} adım tamamlandı
                </div>
              </div>
            </Space>
          </div>
        }
        extra={
          <Tooltip title="Küçült">
            <Button 
              type="text" 
              size="small" 
              icon={<MinusOutlined />}
              onClick={minimizeGuideAndSave}
              className="setup-todo-minimize-btn"
            />
          </Tooltip>
        }
      >
        {/* Progress Bar ve diğer içerikler... */}
        <div className="setup-todo-progress">
          <Progress 
            percent={progress} 
            strokeColor={{
              '0%': '#7338a0',
              '100%': '#9c6cd4'
            }}
            trailColor="rgba(115, 56, 160, 0.1)"
            size="small"
            showInfo={false}
          />
          <div className="setup-todo-progress-text">
            %{progress} Tamamlandı
          </div>
        </div>
        <List
          className="setup-todo-list"
          size="small"
          dataSource={todoItems}
          renderItem={(item) => (
            <List.Item
              className={`setup-todo-item ${item.completed ? 'completed' : 'pending'}`}
              onClick={() => handleItemClick(item)}
            >
              <List.Item.Meta
                avatar={
                  <div className="setup-todo-item-avatar">
                    {item.completed ? (
                      <CheckCircleOutlined className="completed-icon" />
                    ) : (
                      <Avatar 
                        size="small" 
                        icon={getIcon(item.icon)}
                        className="pending-avatar"
                      />
                    )}
                  </div>
                }
                title={
                  <div className="setup-todo-item-title">
                    {item.title}
                    {!item.completed && (
                      <span className="setup-todo-priority">
                        Adım {item.priority}
                      </span>
                    )}
                  </div>
                }
                description={
                  <div className="setup-todo-item-description">
                    {item.description}
                  </div>
                }
              />
              {!item.completed && (
                <Button 
                  type="primary" 
                  size="small"
                  className="setup-todo-action-btn"
                >
                  Başla
                </Button>
              )}
            </List.Item>
          )}
        />
        {progress === 100 && (
          <div className="setup-todo-completed">
            <CheckCircleOutlined className="completed-main-icon" />
            <div className="completed-text">
              <strong>Tebrikler!</strong> Kurulum tamamlandı 🎉
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default SetupTodo;
